import {
	ReactElement,
	ReadonlyRefObject,
	useCallback,
	useEffect,
	useMemo,
	useState
} from "react";
import { SettingsPage } from "../SettingsPage";
import {
	Checkbox,
	Form,
	FormItem,
	Tabs,
	ToolbarAction,
	ValidationError
} from "client/Components";
import { localize } from "client_legacy/ndaw/culture";
import {
	OfficeAppsSavingModel,
	PermissionsMode,
	SharePointAuthenticationType,
	SharePointSettingsNode,
	SharePointSettingsNodeId,
	SharePointSettingsSavingModel,
	SharePointSettingsViewModel,
	SiteAvailabilityFilterKind,
	SiteManagementLanguage,
	SyncClient,
	TemplateAvailabilityKind,
	disable,
	getSharePointSettingsViewModel,
	loadHierarchy,
	saveSettings
} from "client/Api/SharePointSettings/sharepointSettings";
import { IFormHandler, useFormHandler } from "client/Forms";
import {
	IRootValidation,
	IValidationConfiguration,
	InputValidationResult,
	InputValidationResultKind,
	createValidationConfiguration
} from "client/Validation";
import { OfficeWebAppOpenTarget } from "client/Api/Documents/SharePoint/common";
import { OfficeDocumentEditor } from "client/Api/Documents/common";
import { SettingsTab } from "./SettingsTab";
import { OfficeAppsTab } from "./OfficeAppsTab";
import { IpAddressRange } from "client/Api/common";
import { UsersAndUserGroupsDataModel } from "client/Api/UserGroupsManagement/userGroupsManagement";
import { SiteManagementTab } from "./SiteManagementTab";
import {
	isCancelledError
} from "client_legacy/ndaw/cancellation";
import {
	AlertManager,
	MediaQueryKind,
	useAlerts,
	useSynchronizedRef,
	useUnmountedCancellation,
	useWorkspaceMediaQuery
} from "client/Hooks";
import { ObjectWithClientId, setClientId } from "client/Misc/clientId";
import { useActiveSettingsMenuItem } from "../SidebarMenu/sidebarMenuSelectionStore";
import { ActionKind, buildActionsForTopBar } from "client_legacy/ndaw/actions";
import { reloadCurrentPage } from "client_legacy/ndaw/navigator";
import { IObjectValidationStepBuilder, configureValidationStep } from "client/Validation/steps";
import { SiteAvailabilityTab } from "./SiteAvailabilityTab";
import { hasImplicitDependencyOn } from "client/Misc/reactUtils";
import {
	isNotEmpty,
	isNotNullNorUndefined,
	isUrl
} from "client/Validation/rules";
import { handlePageDataLoadingError } from "client/Features/ErrorPage/errorStore";
import { PathRedirect } from "client/Validation/serverErrors";
import { DotPathWildcardPattern } from "client/Misc/dotPathWildcardPattern";
import { SettingsPageKind } from "client/Api/SettingsLayout/settingsLayout";
import { AiAssistantFabSpacing } from "client/Features/ChatAssistant/AiAssistantFabSpacing";

export const SharePointSettingsPage = (): ReactElement => {
	const [isBusy, setIsBusy] = useState<boolean>(true);

	const [viewModelData, setViewModelData] = useState<ViewModelData>(
		(): ViewModelData => ({
			wasLoadedFromBackend: false,
			usersAndUserGroupsData: {
				users: [],
				userGroups: []
			},
			availableLanguages: [],
			availableAuthenticationTypes: [],
			isOfficeWebAppsOpenTargetSettingVisible: false,
			nodes: { isSet: false }
		})
	);

	const [formData, setFormData] = useState<SharePointSettingsFormData>(initialFormData);

	const globalCancellation: AbortSignal = useUnmountedCancellation();
	const alertManager: AlertManager = useAlerts();
	const isResponsive: boolean = useWorkspaceMediaQuery(MediaQueryKind.Responsive);
	useActiveSettingsMenuItem(SettingsPageKind.Sharepoint);

	const formHandler: IFormHandler<SharePointSettingsFormData> = useFormHandler(
		formData,
		setFormData,
		validationConfiguration
	);

	const validationRef: ReadonlyRefObject<IRootValidation<SharePointSettingsFormData>> = useSynchronizedRef(formHandler.validation);

	useEffect(
		(): (() => void) => {
			const load = async (signal: AbortSignal): Promise<void> => {
				setIsBusy(true);

				try {
					const viewModel: SharePointSettingsViewModel = await getSharePointSettingsViewModel(
						signal
					)
						.catch(handlePageDataLoadingError);

					setViewModelData({
						wasLoadedFromBackend: true,
						usersAndUserGroupsData: viewModel.officeAppsSettings
							.openLocalPermissions
							.usersAndUserGroupsData,
						availableAuthenticationTypes: viewModel.availableAuthenticationTypes,
						availableLanguages: viewModel.siteManagementSettings
							.availableLanguages,
						isOfficeWebAppsOpenTargetSettingVisible: viewModel.officeAppsSettings
							.isOfficeWebAppsOpenTargetSettingVisible,
						nodes: { isSet: false }
					});

					setFormData(convertToFormData(viewModel) );

					setIsBusy(false);
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}

					alertManager.showError(localize("_GeneralErrorMessage"));

					setIsBusy(false);
				}
			};

			const cancellation = new AbortController();
			load(cancellation.signal);
			return () => cancellation.abort();
		},
		[alertManager]
	);

	const loadSiteHierarchy = useCallback(
		async (): Promise<void> => {
			try {
				setIsBusy(true);

				const {
					authenticationType,
					url,
					useFasterLoading
				} = formData;

				if (!authenticationType) {
					throw new Error("No authentication type selected");
				}

				const rootNodes: readonly SharePointSettingsNode[] = await loadHierarchy(
					{
						authenticationType,
						url,
						useFasterLoading
					},
					globalCancellation
				);

				setViewModelData(
					(x) => ({
						...x,
						nodes: {
							isSet: true,
							value: rootNodes
						}
					})
				);

				setIsBusy(false);
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				validationRef.current
					.tryProcessServerError(e, pathRedirects);

				alertManager.showError(localize("_CouldNotRetrieveSharePointSitesPleaseTryToRefreshTheSiteOverviewAndTryAgain"));

				setIsBusy(false);
			}
		},
		[
			alertManager,
			formData,
			globalCancellation,
			validationRef
		]
	);

	const saveHandler = useCallback(
		async (): Promise<void> => {
			if (isBusy) {
				return;
			}

			setIsBusy(true);

			const validation: IRootValidation<SharePointSettingsFormData> = formHandler.validation;

			try {
				const validationResult: InputValidationResult = await validation
					.validate(globalCancellation);

				if (validationResult.kind !== InputValidationResultKind.Valid) {
					setIsBusy(false);
					alertManager.showError(localize("_ChangesNotSaved"));
					return;
				}

				if (formData.isEnabled) {
					const model: SharePointSettingsSavingModel = convertToSaveModel(
						formData,
						viewModelData.nodes
							.isSet
					);

					await saveSettings(model);
					alertManager.showSuccess(localize("_ChangesSaved"));
					setIsBusy(false);
				} else {
					await disable(globalCancellation);
					alertManager.showSuccess(localize("_ChangesSaved"));
					// Wait a bit for the alert to be displayed before reloading the page
					setTimeout(() => reloadCurrentPage(), 1000);
				}
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				validation.tryProcessServerError(e, pathRedirects);
				alertManager.showError(localize("_ChangesNotSaved"));

				setIsBusy(false);
			}
		},
		[
			alertManager,
			formData,
			globalCancellation,
			isBusy,
			formHandler.validation,
			viewModelData.nodes
				.isSet
		]
	);

	const actionContext = useMemo(
		(): SharePointSettingsActionContext => ({
			didLoadSuccessful: viewModelData.wasLoadedFromBackend
		}),
		[viewModelData.wasLoadedFromBackend]
	);

	const defaultActions = useMemo(
		(): readonly ToolbarAction<SharePointSettingsActionContext>[] => {
			return [
				{
					kind: ActionKind.Regular,
					name: "save",
					title: localize("_Save"),
					icon: "floppy-disk",
					canExecute: (c) => c.didLoadSuccessful,
					execute: () => saveHandler(),
					isPrimary: true
				},
				{
					kind: ActionKind.Regular,
					name: "cancel",
					title: localize("_Cancel"),
					icon: "xmark",
					canExecute: () => true,
					execute: () => reloadCurrentPage()
				}
			];
		},
		[saveHandler]
	);

	const topBarActions = useMemo(
		(): readonly ToolbarAction<SharePointSettingsActionContext>[] => {
			return buildActionsForTopBar<SharePointSettingsActionContext, ToolbarAction<SharePointSettingsActionContext>>(
				{
					defaultActions,
				},
				false,
				isResponsive,
				false
			);
		},
		[
			defaultActions,
			isResponsive
		]
	);

	useEffect(
		(): void => {
			hasImplicitDependencyOn(formData.url, formData.authenticationType);

			setViewModelData((x) => ({
				...x,
				nodes: {
					isSet: false
				}
			}));
		},
		[
			formData.authenticationType,
			formData.url
		]
	);

	return (
		<SettingsPage
			hasBusyIndicator={isBusy}
			actions={topBarActions}
			actionContext={actionContext}
			title={localize("_SharePointSettings")}
		>
			<SettingsPage.Section
				height="stretch"
			>
				<Form
					onSubmit="NONE"
					height="fill"
				>
					<FormItem>
						<Checkbox
							{...formHandler.for("isEnabled").attachToCheckbox()}
							text={localize("_EnableSharePointDocuments")}
							ellipseText={false}
						/>
						<ValidationError {...formHandler.for("isEnabled").attachToValidationError()} />
					</FormItem>

					{formHandler.for("isEnabled").value && (
						<EnabledSharePointSettings
							formHandler={formHandler}
							viewModeldata={viewModelData}
							onLoadSiteHierarchy={loadSiteHierarchy}
						/>
					)}
				</Form>
			</SettingsPage.Section>
			<div className="preserve-space-for-mobile-plus-button" />
			<AiAssistantFabSpacing />
		</SettingsPage>
	);
};
SharePointSettingsPage.displayName = "SharePointSettingsPage";

export type ViewModelData = {
	wasLoadedFromBackend: boolean;
	usersAndUserGroupsData: UsersAndUserGroupsDataModel;
	availableLanguages: readonly SiteManagementLanguage[];
	availableAuthenticationTypes: readonly SharePointAuthenticationType[];
	isOfficeWebAppsOpenTargetSettingVisible: boolean;
	nodes: OptionalModel<readonly SharePointSettingsNode[]>;
};

export type DocumentActionPermissionsFormData = {
	everyoneHasAccess: boolean;
	userIds: number[];
	userGroupIds: number[];
};

export type SiteAvailabilityFormData = {
	isEnabled: boolean;
	kind: Exclude<SiteAvailabilityFilterKind, SiteAvailabilityFilterKind.None>;
	nodeIds: readonly SharePointSettingsNodeId[];
};

export type OfficeAppsFormData = Omit<OfficeAppsSavingModel, "openLocalPermissions"> & {
	openLocalPermissions: DocumentActionPermissionsFormData;
};

export type SharePointSettingsFormData = Omit<SharePointSettingsSavingModel, "syncClient" | "officeAppsSettings" | "siteAvailability"> & {
	isEnabled: boolean;
	isSyncFilesEnabled: boolean;
	syncClient: Exclude<SyncClient, SyncClient.None>;
	siteAvailability: SiteAvailabilityFormData;
	officeAppsSettings: OfficeAppsFormData;
};

export type EnabledSharePointSettingsProps = {
	formHandler: IFormHandler<SharePointSettingsFormData>;
	viewModeldata: ViewModelData;
	onLoadSiteHierarchy: () => void;
};

export const EnabledSharePointSettings = (props: EnabledSharePointSettingsProps): ReactElement => {
	const {
		formHandler,
		viewModeldata,
		onLoadSiteHierarchy
	} = props;

	const [activeTabId, setActiveTabId] = useState<TabId>("settings");

	const isSiteAvailabilityEnabled: boolean = formHandler.reducer
		.value
		.siteAvailability
		.isEnabled;

	const activeTabIdChangeHandler = useCallback(
		(tabId: TabId): void => {
			setActiveTabId(tabId);

			if (tabId === "site-availability"
				&& isSiteAvailabilityEnabled
				&& !viewModeldata.nodes.isSet
			) {
				onLoadSiteHierarchy();
			}
		},
		[
			isSiteAvailabilityEnabled,
			onLoadSiteHierarchy,
			viewModeldata.nodes
				.isSet
		]
	);

	return (
		<Tabs<TabId>
			height="fill"
			activeTabId={activeTabId}
			onActiveTabIdChange={activeTabIdChangeHandler}
		>
			<Tabs.Tab<TabId>
				{...formHandler.forStep(settingsValidationStep).attachToTab()}
				id="settings"
				title={localize("_Settings")}
			>
				<SettingsTab
					formHandler={formHandler}
					availableAuthenticationTypes={viewModeldata.availableAuthenticationTypes}
				/>
			</Tabs.Tab>

			<Tabs.Tab<TabId>
				{...formHandler.under("officeAppsSettings").attachToTab()}
				id="office-apps"
				title={localize("_OfficeApps")}
			>
				<OfficeAppsTab
					attachable={formHandler.for("officeAppsSettings")}
					usersAndUserGroupsData={viewModeldata.usersAndUserGroupsData}
					isOfficeWebAppsOpenTargetSettingVisible={viewModeldata.isOfficeWebAppsOpenTargetSettingVisible}
				/>
			</Tabs.Tab>

			<Tabs.Tab<TabId>
				{...formHandler.under("siteAvailability").attachToTab()}
				id="site-availability"
				title={localize("_SiteAvailability")}
			>
				<SiteAvailabilityTab
					attachable={formHandler.for("siteAvailability")}
					nodes={viewModeldata.nodes}
					onLoadSiteHierarchy={onLoadSiteHierarchy}
				/>
			</Tabs.Tab>

			<Tabs.Tab<TabId>
				{...formHandler.under("siteManagementSettings").attachToTab()}
				id="site-management"
				title={localize("_SiteManagement")}
			>
				<SiteManagementTab
					attachable={formHandler.for("siteManagementSettings")}
					availableLanguages={viewModeldata.availableLanguages}
				/>
			</Tabs.Tab>
		</Tabs>
	);
};
EnabledSharePointSettings.displayName = "EnabledSharePointSettings";

const validationConfiguration: IValidationConfiguration<SharePointSettingsFormData> =
	createValidationConfiguration<SharePointSettingsFormData>();

const enableValidationConfiguration = validationConfiguration.when(x => x.isEnabled);

enableValidationConfiguration.forScalar("url")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty(),
		isUrl({
			allowHttpsOnly: true,
			allowQueryString: false,
			alternativeMessage: localize("_InvalidSharePointUrl")
		})
	);

enableValidationConfiguration.forScalar("authenticationType")
	.add(
		isNotNullNorUndefined()
	);

const settingsValidationStep: IObjectValidationStepBuilder<SharePointSettingsFormData> = configureValidationStep<SharePointSettingsFormData>()
	.add(
		"url",
		"authenticationType",
		"templateAvailabilityKind",
		"isDocumentViewStructureCustomizationEnabled",
		"syncClient",
		"useFasterLoading",
		"isSharePointViewerEnabled"
	);

type TabId =
	| "settings"
	| "office-apps"
	| "site-availability"
	| "site-management";

type SharePointSettingsActionContext = {
	didLoadSuccessful: boolean;
};

function convertToSaveModel(
	formData: SharePointSettingsFormData,
	wasSiteAvailabilityLoaded: boolean
): SharePointSettingsSavingModel {
	const {
		siteManagementSettings,
		officeAppsSettings,
		siteAvailability
	} = formData;

	return {
		authenticationType: formData.authenticationType,
		url: formData.url,
		syncClient: formData.isSyncFilesEnabled
			? formData.syncClient
			: SyncClient.None,
		useFasterLoading: formData.useFasterLoading,
		siteManagementSettings: {
			siteManagementEnabled: siteManagementSettings.siteManagementEnabled,
			defaultDescription: siteManagementSettings.defaultDescription,
			permissions: siteManagementSettings.permissions,
			language: siteManagementSettings.language,
			overrideDescription: siteManagementSettings.overrideDescription,
			overrideLanguage: siteManagementSettings.overrideLanguage,
			overridePermissions: siteManagementSettings.overridePermissions
		},
		isDocumentViewStructureCustomizationEnabled: formData.isDocumentViewStructureCustomizationEnabled,
		siteAvailability: wasSiteAvailabilityLoaded
			? {
				isSet: true,
				value: siteAvailability.isEnabled
					? {
						kind: siteAvailability.kind,
						nodeIds: siteAvailability.nodeIds
					} : {
						kind: SiteAvailabilityFilterKind.None
					}
			}
			: {
				isSet: false
			},
		templateAvailabilityKind: formData.templateAvailabilityKind,
		officeAppsSettings: convertToOfficeAppsSaveModel(officeAppsSettings),
		isSharePointViewerEnabled: formData.isSharePointViewerEnabled
	};
}

function convertToOfficeAppsSaveModel(officeAppsSettings: OfficeAppsFormData): OfficeAppsSavingModel {
	const {
		officeWebAppOpenTarget,
		allowToOpenInLocalApp,
		defaultOfficeDocumentEditor,
		openLocalPermissions,
		openLocalConditionalAccessSettings
	} = officeAppsSettings;

	return {
		officeWebAppOpenTarget: officeWebAppOpenTarget,
		allowToOpenInLocalApp: allowToOpenInLocalApp,
		...allowToOpenInLocalApp
			? {
				defaultOfficeDocumentEditor: defaultOfficeDocumentEditor,
				openLocalPermissions: openLocalPermissions.everyoneHasAccess
					? {
						everyoneHasAccess: true
					} : {
						everyoneHasAccess: false,
						userIds: openLocalPermissions.userIds,
						userGroupIds: openLocalPermissions.userGroupIds,
					},
				openLocalConditionalAccessSettings: {
					restrictIp: openLocalConditionalAccessSettings.restrictIp,
					blacklistIpRanges: openLocalConditionalAccessSettings.blacklistIpRanges,
					whitelistIpRanges: openLocalConditionalAccessSettings.whitelistIpRanges,
				}
			}
			: {
				defaultOfficeDocumentEditor: OfficeDocumentEditor.Online,
				openLocalPermissions: {
					everyoneHasAccess: true
				},
				openLocalConditionalAccessSettings: {
					restrictIp: false,
					blacklistIpRanges: [],
					whitelistIpRanges: []
				}
			}
	};
}

const initialFormData: SharePointSettingsFormData = {
	isEnabled: false,
	authenticationType: null,
	url: "",
	isSyncFilesEnabled: false,
	syncClient: SyncClient.OneDrive,
	useFasterLoading: false,
	siteManagementSettings: {
		siteManagementEnabled: false,
		defaultDescription: "",
		permissions: PermissionsMode.Inherit,
		language: -1,
		overrideDescription: false,
		overrideLanguage: false,
		overridePermissions: false
	},
	isDocumentViewStructureCustomizationEnabled: false,
	siteAvailability: {
		isEnabled: false,
		kind: SiteAvailabilityFilterKind.Blacklist,
		nodeIds: []
	},
	templateAvailabilityKind: TemplateAvailabilityKind.Library,
	officeAppsSettings: {
		officeWebAppOpenTarget: OfficeWebAppOpenTarget.NewOffice365Tab,
		defaultOfficeDocumentEditor: OfficeDocumentEditor.Online,
		allowToOpenInLocalApp: false,
		openLocalPermissions: {
			everyoneHasAccess: false,
			userGroupIds: [],
			userIds: []
		},
		openLocalConditionalAccessSettings: {
			restrictIp: false,
			whitelistIpRanges: [],
			blacklistIpRanges: []
		}
	},
	isSharePointViewerEnabled: false
};

function convertToFormData(viewModel: SharePointSettingsViewModel): SharePointSettingsFormData {
	const {
		isEnabled,
		url,
		useFasterLoading,
		isDocumentViewStructureCustomizationEnabled,
		templateAvailabilityKind,
		isSharePointViewerEnabled,
		availableAuthenticationTypes,
		siteManagementSettings,
		officeAppsSettings,
		siteAvailability
	} = viewModel;


	let authenticationType: SharePointAuthenticationType = viewModel.authenticationType;

	if (!availableAuthenticationTypes.includes(viewModel.authenticationType)) {
		authenticationType = availableAuthenticationTypes[0];
	}

	return {
		isEnabled,
		authenticationType,
		url,
		isSyncFilesEnabled: viewModel.syncClient !== SyncClient.None,
		syncClient: viewModel.syncClient === SyncClient.None
			? SyncClient.Groove
			: viewModel.syncClient,
		useFasterLoading,
		siteManagementSettings: {
			siteManagementEnabled: siteManagementSettings.siteManagementEnabled,
			defaultDescription: siteManagementSettings.defaultDescription,
			permissions: siteManagementSettings.permissions,
			language: siteManagementSettings.language,
			overrideDescription: siteManagementSettings.overrideDescription,
			overrideLanguage: siteManagementSettings.overrideLanguage,
			overridePermissions: siteManagementSettings.overridePermissions
		},
		isDocumentViewStructureCustomizationEnabled,
		siteAvailability: {
			isEnabled: siteAvailability.kind !== SiteAvailabilityFilterKind.None,
			kind: siteAvailability.kind === SiteAvailabilityFilterKind.None
				? SiteAvailabilityFilterKind.Blacklist
				: siteAvailability.kind,
			nodeIds: siteAvailability.kind !== SiteAvailabilityFilterKind.None
				? siteAvailability.nodeIds
				: []
		},
		templateAvailabilityKind,
		officeAppsSettings: {
			officeWebAppOpenTarget: officeAppsSettings.officeWebAppOpenTarget ?? OfficeWebAppOpenTarget.Popup,
			defaultOfficeDocumentEditor: officeAppsSettings.defaultOfficeDocumentEditor,
			allowToOpenInLocalApp: officeAppsSettings.allowToOpenInLocalApp,
			openLocalPermissions: {
				everyoneHasAccess: officeAppsSettings.openLocalPermissions
					.everyoneHasAccess,
				userGroupIds: officeAppsSettings.openLocalPermissions
					.userGroupIds,
				userIds: officeAppsSettings.openLocalPermissions
					.userIds
			},
			openLocalConditionalAccessSettings: {
				restrictIp: officeAppsSettings.openLocalConditionalAccessSettings
					.whitelistIpRanges
					.length > 0
					|| officeAppsSettings.openLocalConditionalAccessSettings
						.blacklistIpRanges
						.length > 0,
				whitelistIpRanges: officeAppsSettings.openLocalConditionalAccessSettings
					.whitelistIpRanges
					.map((value): ObjectWithClientId<IpAddressRange> => setClientId({ value })),
				blacklistIpRanges: officeAppsSettings.openLocalConditionalAccessSettings
					.blacklistIpRanges
					.map((value): ObjectWithClientId<IpAddressRange> => setClientId({ value }))
			}
		},
		isSharePointViewerEnabled
	};
}

const pathRedirects: readonly PathRedirect[] = [
	{
		pattern: new DotPathWildcardPattern("SiteAvailability.Value.*?"),
		replacement: "SiteAvailability.{0}"
	}
];

export const sharePointSettingsRouting = {
	page: "/SharePointSettings"
};
